import { rememberScoreLimit } from '../helpers/utils/recentScoreLimits';
import {
  useReducer,
  ReactNode,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { GlobalContext } from './global-context';
import rootReducer, { getInitialState } from './rootReducer';
import { updateInput } from '../helpers/utils/helperFunctions';
import {
  recordScoringTransition,
  resetRealGameAnalytics,
} from '../helpers/utils/realGameAnalytics';
import { trackEvent } from '../helpers/utils/analytics';
import { reportProblem } from '../helpers/utils/errorReporting';
import {
  applyTeamMadeTotal,
  clearAutoGeneratedMade,
  isPlayerMadeField,
} from '../helpers/utils/scoringFields';
import {
  rotateNames,
  rotateRound,
  rotateGameState,
} from '../helpers/utils/perspective';
import {
  persistViewerSession,
  clearPersistedViewerSession,
  persistViewerSeat,
  getPersistedViewerSeat,
  hasOwnLocalGame,
} from '../helpers/utils/viewerSession';
import { setFeatureFlag, FEATURE_FLAGS } from '../helpers/utils/featureFlags';
import { loadRealtime } from '../firebase/loadRealtime';
import type {
  UpdateInputArgs,
  Round,
  AppState,
  Names,
  NilSetting,
  Seat,
  SessionRole,
  WinAcknowledgement,
} from '../types';

function onWatchError(err: unknown) {
  console.error('Session subscribe error:', err);
  reportProblem('live_share_failed', { share_stage: 'watch' }, err);
}

const isTeamTotalUpdate = (fieldToUpdate: string) => {
  return fieldToUpdate.includes('team') && fieldToUpdate.includes('Total');
};

const teamNumberFromTotalField = (fieldToUpdate: string): 1 | 2 =>
  fieldToUpdate.includes('team1') ? 1 : 2;

export const StateProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(
    rootReducer,
    undefined as unknown as AppState,
    getInitialState,
  );

  // ── Realtime session state ─────────────────────────────────────────────
  const [role, setRole] = useState<SessionRole>('local');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [seat, setSeat] = useState<Seat>('t1p1');
  const [isViewerSynced, setIsViewerSynced] = useState(false);
  const seatRef = useRef(seat);
  const sessionIdRef = useRef(sessionId);
  const uiModeAppliedRef = useRef(false);

  // Keep refs in sync with state — avoids direct ref mutation during render
  useEffect(() => {
    seatRef.current = seat;
  }, [seat]);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const startLeaderSession = useCallback(async () => {
    const existing = sessionIdRef.current;
    if (existing) {
      setRole('leader');
      return;
    }
    // Bound the whole startup, including loading the SDK and the remote write.
    // Firebase may keep a disconnected write pending indefinitely.
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const id = await Promise.race([
        (async () => {
          const { isRealtimeEnabled, createSession } = await loadRealtime();
          if (!isRealtimeEnabled()) {
            throw new Error('Firebase is not configured');
          }
          return createSession(state);
        })(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error('Sharing startup timed out')),
            10000,
          );
        }),
      ]);
      // A late remote result after timeout must not activate a stale session.
      setSessionId(id);
      setRole('leader');
    } finally {
      clearTimeout(timer);
    }
  }, [state]);

  const joinSession = useCallback((id: string) => {
    setSessionId(id);
    setRole('viewer');
    persistViewerSession(id);
    const persistedSeat = getPersistedViewerSeat();
    if (persistedSeat) setSeat(persistedSeat as Seat);
  }, []);

  const endSession = useCallback(() => {
    setRole('local');
    setSessionId(null);
    setIsViewerSynced(false);
    uiModeAppliedRef.current = false;
    clearPersistedViewerSession();
    if (hasOwnLocalGame()) {
      // This device has its own game: restore it, undoing the hydrated leader
      // board.
      dispatch({ type: 'RESTORE_LOCAL' });
    } else {
      // Fresh device: seed the name form AND the board with the FULL watched
      // game, rotated so the viewer's chosen seat lands in the "you" (t1p1)
      // slot. Leaving must not blank the game — the leader's rounds carry over
      // so "Continue" resumes where they left off. In-memory only, so
      // `hasOwnLocalGame()` stays false and the leave flow lands on the form.
      dispatch({
        type: 'SEED_GAME_FROM_VIEW',
        payload: rotateGameState(state, seat),
      });
    }
    // The chosen seat is deliberately NOT cleared: it's a stable per-device
    // preference ("where am I sitting?"), so leaving a session and rejoining
    // (Undo, or re-opening the link) must not re-prompt for the seat.
  }, [state, seat]);

  // Seat selection is persisted so a viewer who navigates away and back keeps
  // their chosen perspective instead of being re-prompted.
  const setSeatAndPersist = useCallback((nextSeat: Seat) => {
    setSeat(nextSeat);
    persistViewerSeat(nextSeat);
  }, []);

  // Viewer perspective: rotate the four seats so the viewer's own seat is in
  // the "you" position. This preserves left/right correctly (a team mirror
  // would invert them). Team 1 / leader seats simply aren't rotated.
  const isViewer = role === 'viewer';

  const displayNames = useMemo(
    () => (isViewer ? rotateNames(state.names, seat) : state.names),
    [isViewer, state.names, seat],
  );

  const viewCurrentRound = useMemo(
    () =>
      isViewer ? rotateRound(state.currentRound, seat) : state.currentRound,
    [isViewer, state.currentRound, seat],
  );

  const viewRoundHistory = useMemo(
    () =>
      isViewer
        ? state.roundHistory.map((round) => rotateRound(round, seat))
        : state.roundHistory,
    [isViewer, state.roundHistory, seat],
  );

  // Leader: push state to the session on every change (debounced).
  useEffect(() => {
    if (role !== 'leader' || !sessionId) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      loadRealtime()
        .then(({ writeSessionState }) => {
          if (cancelled) return;
          return writeSessionState(sessionId, state);
        })
        .catch((err) => {
          console.error('Failed to write session state:', err);
          reportProblem('live_share_failed', { share_stage: 'write' }, err);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [state, role, sessionId]);

  // Viewer: subscribe to the session and hydrate as the leader writes.
  // The realtime chunk is fetched here, before subscribe, so a shared link
  // still joins and a visit with no session never requests it.
  useEffect(() => {
    if (role !== 'viewer' || !sessionId) return;
    let unsubscribe = () => {};
    let cancelled = false;
    loadRealtime()
      .then(({ subscribeSession }) => {
        if (cancelled) return;
        unsubscribe = subscribeSession(
          sessionId,
          (incoming) => {
            dispatch({ type: 'HYDRATE', payload: incoming });
            setIsViewerSynced(true);
          },
          onWatchError,
          (uiMode) => {
            // Apply the leader's UI mode as the default on first sync only. The
            // viewer can subsequently toggle their own layout via Settings.
            if (uiModeAppliedRef.current) return;
            uiModeAppliedRef.current = true;
            setFeatureFlag(FEATURE_FLAGS.TABLE_ROUND_UI, uiMode);
          },
        );
      })
      .catch(onWatchError);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [role, sessionId]);

  const setCurrentRound = useCallback(
    (args: UpdateInputArgs) => {
      const next = isTeamTotalUpdate(args.fieldToUpdate)
        ? applyTeamMadeTotal(
            args.currentRound,
            teamNumberFromTotalField(args.fieldToUpdate),
            parseInt(args.input as string, 10),
          )
        : isPlayerMadeField(args.fieldToUpdate)
          ? clearAutoGeneratedMade(updateInput(args), args.fieldToUpdate)
          : updateInput(args);
      if (role !== 'viewer') {
        recordScoringTransition(
          args.currentRound,
          next,
          state.roundHistory.length,
          Date.now(),
          () => trackEvent('real_game_qualified'),
        );
      }
      dispatch({ type: 'SET_CURRENT_ROUND', payload: { currentRound: next } });
    },
    [role, state.roundHistory.length],
  );

  const replaceCurrentRound = useCallback(
    (currentRound: Round) => {
      if (role !== 'viewer') {
        recordScoringTransition(
          state.currentRound,
          currentRound,
          state.roundHistory.length,
          Date.now(),
          () => trackEvent('real_game_qualified'),
        );
      }
      dispatch({ type: 'SET_CURRENT_ROUND', payload: { currentRound } });
    },
    [role, state.currentRound, state.roundHistory.length],
  );

  const resetCurrentRound = useCallback(() => {
    dispatch({
      type: 'RESET_CURRENT_ROUND',
    });
  }, []);

  const setRoundHistory = useCallback((newRoundHistory: Round[]) => {
    if (newRoundHistory.length === 0) resetRealGameAnalytics();
    const clonedNewRoundHistory = [...newRoundHistory];
    dispatch({
      type: 'SET_ROUND_HISTORY',
      payload: {
        roundHistory: clonedNewRoundHistory,
      },
    });
  }, []);

  const updatePastRound = useCallback(
    (index: number, update: (round: Round) => Round) => {
      dispatch({ type: 'UPDATE_PAST_ROUND', payload: { index, update } });
    },
    [],
  );

  const setFirstDealerOrder = useCallback((newFirstDealerOrder: string[]) => {
    const clonedNewFirstDealerOrder = [...newFirstDealerOrder];
    dispatch({
      type: 'SET_FIRST_DEALER_ORDER',
      payload: {
        firstDealerOrder: clonedNewFirstDealerOrder,
      },
    });
  }, []);

  const setDealerOverride = useCallback((dealerOverride: string | null) => {
    dispatch({
      type: 'SET_DEALER_OVERRIDE',
      payload: {
        dealerOverride,
      },
    });
  }, []);

  const resetRoundHistory = useCallback(() => {
    resetRealGameAnalytics();
    dispatch({
      type: 'RESET_ROUND_HISTORY',
    });
  }, []);

  const setNames = useCallback(
    (names: Names | ((val: Names) => Names)) => {
      const valueToStore =
        names instanceof Function ? names(state.names) : names;
      dispatch({
        type: 'SET_NAMES',
        payload: { names: valueToStore },
      });
    },
    [state.names],
  );

  const setNilScoringRule = useCallback((rule: NilSetting) => {
    dispatch({
      type: 'SET_NIL_SCORING_RULE',
      payload: { nilScoringRule: rule },
    });
  }, []);

  const applyScoreLimit = useCallback(
    (limit: number | null, type: 'SET_SCORE_LIMIT' | 'START_NEW_GAME') => {
      rememberScoreLimit(limit);
      dispatch({ type, payload: { scoreLimit: limit } });
    },
    [],
  );

  const setScoreLimit = useCallback(
    (limit: number | null) => {
      applyScoreLimit(limit, 'SET_SCORE_LIMIT');
    },
    [applyScoreLimit],
  );

  const setWinAcknowledged = useCallback(
    (winAcknowledged: WinAcknowledgement | null) => {
      dispatch({
        type: 'SET_WIN_ACKNOWLEDGED',
        payload: { winAcknowledged },
      });
    },
    [],
  );

  const startNewGame = useCallback(
    (limit: number | null) => {
      resetRealGameAnalytics();
      applyScoreLimit(limit, 'START_NEW_GAME');
    },
    [applyScoreLimit],
  );

  const globalStore = useMemo(
    () => ({
      setCurrentRound,
      replaceCurrentRound,
      setRoundHistory,
      updatePastRound,
      resetRoundHistory,
      resetCurrentRound,
      setFirstDealerOrder,
      setDealerOverride,
      setNames,
      setNilScoringRule,
      setScoreLimit,
      startNewGame,
      setWinAcknowledged,
      firstDealerOrder: state.firstDealerOrder,
      currentRound: state.currentRound,
      roundHistory: state.roundHistory,
      isFirstGameAmongTeammates: state.isFirstGameAmongTeammates,
      names: state.names,
      nilScoringRule: state.nilScoringRule,
      scoreLimit: state.scoreLimit,
      winAcknowledged: state.winAcknowledged,
      displayNames,
      viewCurrentRound,
      viewRoundHistory,
      role,
      sessionId,
      seat,
      isViewerSynced,
      startLeaderSession,
      joinSession,
      setSeat: setSeatAndPersist,
      endSession,
    }),
    [
      setCurrentRound,
      replaceCurrentRound,
      setRoundHistory,
      updatePastRound,
      resetRoundHistory,
      resetCurrentRound,
      setFirstDealerOrder,
      setDealerOverride,
      setNames,
      setNilScoringRule,
      setScoreLimit,
      startNewGame,
      setWinAcknowledged,
      state.firstDealerOrder,
      state.currentRound,
      state.roundHistory,
      state.isFirstGameAmongTeammates,
      state.names,
      state.nilScoringRule,
      state.scoreLimit,
      state.winAcknowledged,
      displayNames,
      viewCurrentRound,
      viewRoundHistory,
      role,
      sessionId,
      seat,
      isViewerSynced,
      startLeaderSession,
      joinSession,
      setSeatAndPersist,
      endSession,
    ],
  );

  return (
    <GlobalContext.Provider value={globalStore}>
      {children}
    </GlobalContext.Provider>
  );
};
